import { Button, GenericFullscreenDialog } from '@/common'
import InstallationForm from '../../../components/InstallationForm'
import {
  InstallationFormData,
  validateInstallationForm
} from '../../../components/InstallationForm.schema'
import { Installation, useUpdateInstallation } from '@/features/suppliers/hooks/installations'
import useI18n from '@/shared/hooks/usei18n'
import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'

export default function InstallationEditDialog({
  isOpen,
  close,
  installation,
  partnershipId
}: {
  isOpen: boolean
  close: () => void
  installation: Installation
  partnershipId: string
}) {
  const { t } = useI18n()

  const { mutateAsync: updateInstallation, isPending: isSaving } = useUpdateInstallation(
    partnershipId,
    installation.id
  )

  const [formData, setFormData] = useState<InstallationFormData>({
    name: '',
    goodsFamily: '',
    street: '',
    streetNumber: '',
    street2: '',
    postCode: '',
    city: '',
    poBox: '',
    country: '',
    unlocode: '',
    longitude: 0,
    latitude: 0
  })

  const validationResult = useCallback(() => {
    return validateInstallationForm(formData)
  }, [formData])

  const resetFormData = useCallback(() => {
    setFormData({
      name: installation.name,
      goodsFamily: installation.goodsFamily,
      street: installation.street,
      streetNumber: installation.streetNumber,
      street2: installation.street2,
      postCode: installation.postCode,
      city: installation.city,
      poBox: installation.poBox,
      country: installation.country,
      unlocode: installation.unlocode,
      longitude: installation.longitude,
      latitude: installation.latitude
    })
  }, [installation])

  useEffect(() => {
    resetFormData()
  }, [resetFormData, installation])

  const onClose = useCallback(() => {
    // Reset form data to original installation values when closing
    resetFormData()
    close()
  }, [close, resetFormData])

  const isSaveDisabled = useMemo(() => {
    const validation = validationResult()
    return isSaving || !validation.success
  }, [isSaving, validationResult])

  const onSave = useCallback(async () => {
    if (!installation) {return}

    try {
      const validation = validationResult()

      if (!validation.success) {
        return
      }
      await updateInstallation({
        name: validation.data.name,
        goodsFamily: validation.data.goodsFamily,
        basisForReportingPeriod: installation.basisForReportingPeriod,
        startMonth: installation.startMonth,
        monthInterval: installation.monthInterval,
        street: validation.data.street,
        streetNumber: validation.data.streetNumber,
        street2: validation.data.street2,
        postCode: validation.data.postCode,
        city: validation.data.city,
        poBox: validation.data.poBox,
        country: validation.data.country,
        unlocode: validation.data.unlocode,
        longitude: validation.data.longitude,
        latitude: validation.data.latitude
      })
      toast.success(
        t('installations:editInstallationDialog.notifications.installationUpdatedSuccessfully'),
        {
          position: 'top-right'
        }
      )
    } catch (error) {
      toast.error(
        t('installations:editInstallationDialog.notifications.failedToUpdateInstallation'),
        {
          position: 'top-right'
        }
      )
      console.error(error)
    }
    onClose()
  }, [t, updateInstallation, installation, validationResult, onClose])

  return (
    <GenericFullscreenDialog
      isOpen={isOpen}
      close={close}
      title={t('installations:editInstallationDialog.title')}
      actions={
        <div className="flex justify-end gap-2">
          <Button variant="neutral" onClick={onClose}>
            {t('common:actions.cancel')}
          </Button>
          <Button disabled={isSaving || isSaveDisabled} onClick={onSave}>
            {t('installations:editInstallationDialog.actions.save')}
          </Button>
        </div>
      }
    >
      <InstallationForm data={formData} isEditMode onChange={setFormData} />
    </GenericFullscreenDialog>
  )
}
